'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  Download,
  Eraser,
  FilePlus2,
  Image as ImageIcon,
  Pencil,
  Plus,
  RotateCw,
  Trash2,
  Type as TypeIcon,
  Upload,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';

import {
  blankPage,
  buildPdf,
  newId,
  pagesOfFile,
  parseRange,
  BLANK_SIZE,
  type Overlay,
  type PageRef,
} from '@/lib/pdf/pdf-doc';

/**
 * Рабочее место PDF-редактора.
 *
 * Всё происходит во вкладке: файл читается через FileReader, страницы
 * рисует pdf.js, итоговый документ собирает pdf-lib. Ни одного запроса
 * на сервер здесь нет и быть не должно — это и есть обещание DataHunter.
 *
 * Страница показана крупно, а миниатюры отведены в узкую полосу слева.
 * Одни миниатюры не годились: поставить надпись можно только туда, куда
 * попадаешь курсором, а на картинке в сто пикселей человек не различает
 * даже, какой это абзац.
 *
 * pdf.js подключается динамически, уже после монтирования. Он тянет за
 * собой воркер и обращается к DOMMatrix, которого нет при серверном
 * рендере, поэтому статический импорт ронял бы саму страницу, а не
 * только предпросмотр.
 */

interface Rendered {
  /** Идентификатор страницы из `pages`. */
  id: string;
  /** Отрисованная страница как data URL. */
  url: string;
  width: number;
  height: number;
}

/** Что делает щелчок по странице. */
type Tool = 'select' | 'text' | 'fill' | 'draw';

/** Надпись, отрисованная браузером: кириллица без возни со шрифтами. */
async function renderTextToPng(
  overlay: Overlay,
): Promise<{ png: Uint8Array; width: number; height: number }> {
  const scale = 2;
  const measure = document.createElement('canvas').getContext('2d');
  if (!measure) throw new Error('canvas недоступен');

  const font = `${String(overlay.size)}px system-ui, sans-serif`;
  measure.font = font;
  const width = Math.max(1, Math.ceil(measure.measureText(overlay.value).width));
  const height = Math.ceil(overlay.size * 1.3);

  const canvas = document.createElement('canvas');
  canvas.width = width * scale;
  canvas.height = height * scale;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas недоступен');

  ctx.scale(scale, scale);
  ctx.font = font;
  ctx.fillStyle = overlay.color;
  ctx.textBaseline = 'top';
  ctx.fillText(overlay.value, 0, 0);

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, 'image/png');
  });
  if (!blob) throw new Error('не удалось отрисовать надпись');

  return {
    png: new Uint8Array(await blob.arrayBuffer()),
    width: canvas.width,
    height: canvas.height,
  };
}

/**
 * Чистый лист как готовая «отрисовка».
 *
 * Белый лист незачем гонять через pdf.js: у него нет содержимого, а
 * размер задан заранее. Достаточно посчитать габариты в том же
 * масштабе, а белизну даёт фон холста.
 */
function blankRender(ref: PageRef, scale: number): Rendered {
  const turned = ref.rotation % 180 !== 0;
  const w = (turned ? BLANK_SIZE.height : BLANK_SIZE.width) * scale;
  const h = (turned ? BLANK_SIZE.width : BLANK_SIZE.height) * scale;
  return { id: ref.id, url: '', width: w, height: h };
}

export function PdfEditor({ title }: { title: string }) {
  const t = useTranslations('dh_pdf');

  const [sources, setSources] = useState<ArrayBuffer[]>([]);
  const [pages, setPages] = useState<PageRef[]>([]);
  const [thumbs, setThumbs] = useState<Rendered[]>([]);
  const [big, setBig] = useState<Rendered | null>(null);
  const [overlays, setOverlays] = useState<Overlay[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [range, setRange] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [tool, setTool] = useState<Tool>('select');
  const [text, setText] = useState('');
  const [textSize, setTextSize] = useState(18);
  const [textColor, setTextColor] = useState('#111111');
  const [fillColor, setFillColor] = useState('#ffffff');
  const [drawColor, setDrawColor] = useState('#d92d20');
  const [drawWidth, setDrawWidth] = useState(3);
  const [zoom, setZoom] = useState(1);

  const fileInput = useRef<HTMLInputElement>(null);
  const imageInput = useRef<HTMLInputElement>(null);
  const dragId = useRef<string | null>(null);
  /** Заливка или росчерк, который сейчас ведут протяжкой. */
  const drawing = useRef<{ id: string; x: number; y: number; kind: 'fill' | 'draw' } | null>(null);
  /**
   * Точки текущего росчерка.
   *
   * Копить их через setState нельзя: браузер шлёт mousemove чаще,
   * чем React успевает применить обновление, и первые точки ложились в
   * ещё не созданное наложение и пропадали. При быстром росчерке так
   * терялась вся линия целиком.
   */
  const strokePoints = useRef<{ x: number; y: number }[]>([]);
  /**
   * Наложение, которое тащат, и смещение курсора внутри него.
   *
   * Смещение запоминается, чтобы предмет не прыгал углом под курсор
   * в момент захвата: человек берёт картинку за середину и ждёт,
   * что она останется в той же точке относительно пальца.
   */
  const moving = useRef<{ id: string; dx: number; dy: number } | null>(null);

  const current = useMemo(
    () => pages.find((page) => page.id === selected) ?? null,
    [pages, selected],
  );

  /** Первая открытая страница выбирается сама: пустой холст бесполезен. */
  useEffect(() => {
    if (pages.length === 0) {
      setSelected(null);
      return;
    }
    setSelected((prev) =>
      prev && pages.some((p) => p.id === prev) ? prev : (pages[0]?.id ?? null),
    );
  }, [pages]);

  /** pdf.js один на весь компонент: загружать модуль на каждый рендер незачем. */
  const loadPdfjs = useCallback(async () => {
    const pdfjs = await import('pdfjs-dist');
    pdfjs.GlobalWorkerOptions.workerSrc = new URL(
      'pdfjs-dist/build/pdf.worker.min.mjs',
      import.meta.url,
    ).toString();
    return pdfjs;
  }, []);

  /** Миниатюры: мелко и для всех страниц сразу. */
  useEffect(() => {
    if (pages.length === 0) {
      setThumbs([]);
      return;
    }

    // Флаг в объекте, а не отдельная переменная: TypeScript сузил бы
    // `let cancelled = false` до литерала и считал проверку заведомо
    // ложной, хотя её меняет функция очистки эффекта.
    const state = { cancelled: false };

    void (async () => {
      const pdfjs = await loadPdfjs();
      const docs = await Promise.all(
        sources.map((bytes) => pdfjs.getDocument({ data: bytes.slice(0) }).promise),
      );

      const drawn: Rendered[] = [];
      for (const ref of pages) {
        if (ref.fileIndex < 0) {
          // Чистый лист рисуется белым прямоугольником мимо pdf.js:
          // разбирать его нечего, а размер известен заранее.
          drawn.push(blankRender(ref, 0.25));
          continue;
        }
        const doc = docs[ref.fileIndex];
        if (!doc) continue;
        const page = await doc.getPage(ref.pageIndex + 1);
        const viewport = page.getViewport({ scale: 0.25, rotation: ref.rotation });
        const canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) continue;
        await page.render({ canvas, canvasContext: ctx, viewport }).promise;
        drawn.push({
          id: ref.id,
          url: canvas.toDataURL(),
          width: viewport.width,
          height: viewport.height,
        });
      }

      if (!state.cancelled) setThumbs(drawn);
    })().catch(() => {
      if (!state.cancelled) setError(t('error_render'));
    });

    return () => {
      state.cancelled = true;
    };
  }, [pages, sources, loadPdfjs, t]);

  /**
   * Крупная страница: только выбранная и в своём разрешении.
   *
   * Отдельно от миниатюр намеренно. Рисовать все страницы в большом
   * масштабе значило бы держать в памяти десятки полноразмерных
   * растров, а документ на сотню страниц просто убивал бы вкладку.
   */
  useEffect(() => {
    if (!current) {
      setBig(null);
      return;
    }

    const state = { cancelled: false };

    void (async () => {
      if (current.fileIndex < 0) {
        if (!state.cancelled) setBig(blankRender(current, 1.5 * zoom));
        return;
      }

      const pdfjs = await loadPdfjs();
      const bytes = sources[current.fileIndex];
      if (!bytes) return;

      const doc = await pdfjs.getDocument({ data: bytes.slice(0) }).promise;
      const page = await doc.getPage(current.pageIndex + 1);
      const viewport = page.getViewport({ scale: 1.5 * zoom, rotation: current.rotation });
      const canvas = document.createElement('canvas');
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      await page.render({ canvas, canvasContext: ctx, viewport }).promise;

      if (!state.cancelled) {
        setBig({
          id: current.id,
          url: canvas.toDataURL(),
          width: viewport.width,
          height: viewport.height,
        });
      }
    })().catch(() => {
      if (!state.cancelled) setError(t('error_render'));
    });

    return () => {
      state.cancelled = true;
    };
  }, [current, sources, zoom, loadPdfjs, t]);

  const addFiles = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;
      setBusy(true);
      setError(null);

      try {
        const added: ArrayBuffer[] = [];
        const addedPages: PageRef[] = [];

        for (const file of Array.from(files)) {
          const bytes = await file.arrayBuffer();
          const index = sources.length + added.length;
          addedPages.push(...(await pagesOfFile(bytes, index)));
          added.push(bytes);
        }

        setSources((prev) => [...prev, ...added]);
        setPages((prev) => [...prev, ...addedPages]);
      } catch {
        setError(t('error_open'));
      } finally {
        setBusy(false);
      }
    },
    [sources.length, t],
  );

  const rotate = useCallback((id: string) => {
    setPages((prev) =>
      prev.map((page) =>
        page.id === id ? { ...page, rotation: (page.rotation + 90) % 360 } : page,
      ),
    );
  }, []);

  const remove = useCallback((id: string) => {
    setPages((prev) => prev.filter((page) => page.id !== id));
    setOverlays((prev) => prev.filter((item) => item.pageId !== id));
  }, []);

  /** Перестановка перетаскиванием: страница встаёт на место цели. */
  const dropOn = useCallback((targetId: string) => {
    const sourceId = dragId.current;
    dragId.current = null;
    if (!sourceId || sourceId === targetId) return;

    setPages((prev) => {
      const from = prev.findIndex((page) => page.id === sourceId);
      const to = prev.findIndex((page) => page.id === targetId);
      if (from === -1 || to === -1) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      if (!moved) return prev;
      next.splice(to, 0, moved);
      return next;
    });
  }, []);

  /** Оставить только страницы из диапазона: извлечение как отбор. */
  const keepRange = useCallback(() => {
    const keep = new Set(parseRange(range, pages.length));
    if (keep.size === 0) return;
    setPages((prev) => prev.filter((_, index) => keep.has(index)));
  }, [range, pages.length]);

  const addImage = useCallback(
    async (files: FileList | null) => {
      const file = files?.[0];
      if (!file || !selected) return;

      const url = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          // readAsDataURL всегда даёт строку; проверка нужна типам, а не делу.
          resolve(typeof reader.result === 'string' ? reader.result : '');
        };
        reader.onerror = () => {
          reject(new Error('read'));
        };
        reader.readAsDataURL(file);
      });

      setOverlays((prev) => [
        ...prev,
        {
          id: newId(),
          pageId: selected,
          x: 0.1,
          y: 0.1,
          kind: 'image',
          value: url,
          size: 0,
          color: '#000000',
          width: 0.3,
        },
      ]);
    },
    [selected],
  );

  /** Доля страницы под курсором: координаты наложений живут в долях. */
  const pointAt = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    return {
      x: (event.clientX - box.left) / box.width,
      y: (event.clientY - box.top) / box.height,
    };
  }, []);

  const onCanvasDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!selected) return;
      const { x, y } = pointAt(event);

      if (tool === 'draw') {
        const id = newId();
        drawing.current = { id, x, y, kind: 'draw' };
        strokePoints.current = [{ x, y }];
        setOverlays((prev) => [
          ...prev,
          {
            id,
            pageId: selected,
            x: 0,
            y: 0,
            kind: 'draw',
            value: '',
            size: drawWidth,
            color: drawColor,
            width: 0,
            points: [{ x, y }],
          },
        ]);
        return;
      }

      if (tool !== 'fill') return;
      const id = newId();
      drawing.current = { id, x, y, kind: 'fill' };

      setOverlays((prev) => [
        ...prev,
        {
          id,
          pageId: selected,
          x,
          y,
          kind: 'fill',
          value: '',
          size: 0,
          color: fillColor,
          width: 0,
          height: 0,
        },
      ]);
    },
    [selected, tool, fillColor, drawColor, drawWidth, pointAt],
  );

  /** Захват готового наложения, чтобы перетащить его на место. */
  const onOverlayDown = useCallback(
    (event: React.MouseEvent<HTMLElement>, item: Overlay) => {
      // Росчерк кистью не таскается: его коробка перекрывала бы
      // полстраницы и мешала рисовать рядом.
      if (item.kind === 'draw') return;
      if (tool !== 'select') return;
      event.stopPropagation();

      const box = event.currentTarget.parentElement?.getBoundingClientRect();
      if (!box) return;
      moving.current = {
        id: item.id,
        dx: (event.clientX - box.left) / box.width - item.x,
        dy: (event.clientY - box.top) / box.height - item.y,
      };
    },
    [tool],
  );

  const onCanvasMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const dragged = moving.current;
      if (dragged) {
        const { x, y } = pointAt(event);
        setOverlays((prev) =>
          prev.map((item) =>
            item.id === dragged.id
              ? // Зажатие в пределах листа: вытащенное за край наложение
                // просто не попадёт в файл, и человек решит, что оно пропало.
                {
                  ...item,
                  x: Math.min(0.999, Math.max(0, x - dragged.dx)),
                  y: Math.min(0.999, Math.max(0, y - dragged.dy)),
                }
              : item,
          ),
        );
        return;
      }

      const active = drawing.current;
      if (!active) return;
      const { x, y } = pointAt(event);

      if (active.kind === 'draw') {
        strokePoints.current = [...strokePoints.current, { x, y }];
        const points = strokePoints.current;
        setOverlays((prev) =>
          prev.map((item) => (item.id === active.id ? { ...item, points } : item)),
        );
        return;
      }

      setOverlays((prev) =>
        prev.map((item) =>
          item.id === active.id
            ? {
                ...item,
                x: Math.min(active.x, x),
                y: Math.min(active.y, y),
                width: Math.abs(x - active.x),
                height: Math.abs(y - active.y),
              }
            : item,
        ),
      );
    },
    [pointAt],
  );

  const onCanvasUp = useCallback(() => {
    moving.current = null;

    const active = drawing.current;
    drawing.current = null;
    if (!active) return;

    if (active.kind === 'draw') {
      const points = strokePoints.current;
      strokePoints.current = [];
      // Точка без движения — это промах, а не линия: рисовать нечего,
      // а в списке она бы висела невидимой записью.
      setOverlays((prev) =>
        prev.flatMap((item) => {
          if (item.id !== active.id) return [item];
          return points.length > 1 ? [{ ...item, points }] : [];
        }),
      );
      return;
    }

    // Случайный щелчок вместо протяжки не должен оставлять невидимую
    // заливку нулевого размера: её потом не найти и не удалить.
    setOverlays((prev) =>
      prev.filter(
        (item) => item.id !== active.id || (item.width > 0.005 && (item.height ?? 0) > 0.005),
      ),
    );
  }, []);

  /** Щелчок по крупной странице: поставить надпись там, куда попали. */
  const onCanvasClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!selected || tool !== 'text' || text.trim() === '') return;
      const { x, y } = pointAt(event);

      setOverlays((prev) => [
        ...prev,
        {
          id: newId(),
          pageId: selected,
          x,
          y,
          kind: 'text',
          value: text,
          size: textSize,
          color: textColor,
          width: 0.3,
        },
      ]);
      setText('');
      setTool('select');
    },
    [selected, tool, text, textSize, textColor, pointAt],
  );

  const removeOverlay = useCallback((id: string) => {
    setOverlays((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const download = useCallback(async () => {
    if (pages.length === 0) return;
    setBusy(true);
    setError(null);

    try {
      const bytes = await buildPdf({ sources, pages, overlays, renderText: renderTextToPng });
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'datahunter.pdf';
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      setError(t('error_save'));
    } finally {
      setBusy(false);
    }
  }, [sources, pages, overlays, t]);

  const mine = overlays.filter((item) => item.pageId === selected);

  return (
    <div className="max-w-[1440px] mx-auto px-6 py-10">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold">{title}</h1>
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={fileInput}
            type="file"
            accept="application/pdf"
            multiple
            className="hidden"
            data-testid="pdf-file-input"
            onChange={(e) => {
              void addFiles(e.target.files);
              e.target.value = '';
            }}
          />
          <button
            onClick={() => fileInput.current?.click()}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-[var(--color-border)] hover:border-[var(--color-text-muted)] text-sm transition-colors"
            data-testid="pdf-open"
          >
            <Upload className="w-4 h-4" />
            {pages.length === 0 ? t('open') : t('add_file')}
          </button>
          <button
            onClick={() => {
              void download();
            }}
            disabled={pages.length === 0 || busy}
            className="dh-cta inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-colors disabled:opacity-40"
            data-testid="pdf-download"
          >
            <Download className="w-4 h-4" />
            {busy ? t('working') : t('download')}
          </button>
        </div>
      </div>

      {error && (
        <p className="mb-6 text-sm text-red-500" data-testid="pdf-error">
          {error}
        </p>
      )}

      {pages.length === 0 ? (
        <div
          className="rounded-2xl border border-dashed border-[var(--color-border)] bg-[var(--color-bg-card)] px-6 py-20 text-center"
          onDragOver={(e) => {
            e.preventDefault();
          }}
          onDrop={(e) => {
            e.preventDefault();
            void addFiles(e.dataTransfer.files);
          }}
          data-testid="pdf-dropzone"
        >
          <FilePlus2 className="w-8 h-8 mx-auto mb-4 text-[var(--color-text-muted)]" />
          <p className="text-[var(--color-text-muted)]">{t('empty')}</p>
        </div>
      ) : (
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Полоса миниатюр: порядок страниц и переход между ними. */}
          <aside className="lg:w-40 shrink-0">
            <p className="text-xs text-[var(--color-text-muted)] mb-3" data-testid="pdf-count">
              {t('pages_count', { count: pages.length })}
            </p>
            <button
              onClick={() => {
                const page = blankPage();
                setPages((prev) => [...prev, page]);
                setSelected(page.id);
              }}
              className="w-full mb-3 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl border border-dashed border-[var(--color-border)] text-xs hover:border-[var(--color-text-muted)]"
              data-testid="pdf-add-blank"
            >
              <Plus className="w-3.5 h-3.5" />
              {t('add_blank')}
            </button>
            <div className="flex lg:flex-col gap-3 overflow-x-auto lg:overflow-y-auto lg:max-h-[70vh] pb-2">
              {pages.map((page, index) => {
                const thumb = thumbs.find((item) => item.id === page.id);

                return (
                  <div
                    key={page.id}
                    draggable
                    onDragStart={() => {
                      dragId.current = page.id;
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                    }}
                    onDrop={() => {
                      dropOn(page.id);
                    }}
                    onClick={() => {
                      setSelected(page.id);
                    }}
                    data-active={selected === page.id}
                    data-testid="pdf-page"
                    className="shrink-0 w-28 rounded-xl border bg-[var(--color-bg-card)] p-1.5 cursor-pointer transition-colors data-[active=true]:border-[var(--color-text)] border-[var(--color-border)] hover:border-[var(--color-text-muted)]"
                  >
                    <div className="flex items-center justify-center bg-white rounded-lg overflow-hidden min-h-[90px]">
                      {thumb ? (
                        thumb.url === '' ? (
                          <div
                            className="w-full bg-white"
                            style={{
                              aspectRatio: `${String(thumb.width)}/${String(thumb.height)}`,
                            }}
                          />
                        ) : (
                          <img src={thumb.url} alt="" className="w-full h-auto" />
                        )
                      ) : (
                        <span className="text-[10px] text-[var(--color-text-muted)]">
                          {t('drawing')}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center justify-between mt-1 px-0.5">
                      <span className="text-[10px] text-[var(--color-text-muted)]">
                        {index + 1}
                      </span>
                      <div className="flex items-center gap-0.5">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            rotate(page.id);
                          }}
                          title={t('rotate')}
                          aria-label={t('rotate')}
                          className="p-1 rounded hover:bg-[var(--color-bg-elevated)]"
                          data-testid="pdf-rotate"
                        >
                          <RotateCw className="w-3 h-3" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            remove(page.id);
                          }}
                          title={t('delete')}
                          aria-label={t('delete')}
                          className="p-1 rounded hover:bg-[var(--color-bg-elevated)]"
                          data-testid="pdf-delete"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </aside>

          {/* Крупная страница: здесь и ставят надписи. */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <button
                onClick={() => {
                  setTool('select');
                }}
                data-active={tool === 'select'}
                className="px-3 py-2 rounded-xl border text-sm transition-colors data-[active=true]:border-[var(--color-text)] data-[active=true]:bg-[var(--color-bg-elevated)] border-[var(--color-border)]"
                data-testid="pdf-tool-select"
              >
                {t('tool_select')}
              </button>
              <button
                onClick={() => {
                  setTool('text');
                }}
                data-active={tool === 'text'}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border text-sm transition-colors data-[active=true]:border-[var(--color-text)] data-[active=true]:bg-[var(--color-bg-elevated)] border-[var(--color-border)]"
                data-testid="pdf-tool-text"
              >
                <TypeIcon className="w-4 h-4" />
                {t('tool_text')}
              </button>
              <button
                onClick={() => {
                  setTool('fill');
                }}
                data-active={tool === 'fill'}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border text-sm transition-colors data-[active=true]:border-[var(--color-text)] data-[active=true]:bg-[var(--color-bg-elevated)] border-[var(--color-border)]"
                data-testid="pdf-tool-fill"
              >
                <Eraser className="w-4 h-4" />
                {t('tool_fill')}
              </button>

              <button
                onClick={() => {
                  setTool('draw');
                }}
                data-active={tool === 'draw'}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border text-sm transition-colors data-[active=true]:border-[var(--color-text)] data-[active=true]:bg-[var(--color-bg-elevated)] border-[var(--color-border)]"
                data-testid="pdf-tool-draw"
              >
                <Pencil className="w-4 h-4" />
                {t('tool_draw')}
              </button>

              <input
                ref={imageInput}
                type="file"
                accept="image/png,image/jpeg"
                className="hidden"
                onChange={(e) => {
                  void addImage(e.target.files);
                  e.target.value = '';
                }}
              />
              <button
                onClick={() => imageInput.current?.click()}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-[var(--color-border)] text-sm hover:border-[var(--color-text-muted)]"
                data-testid="pdf-add-image"
              >
                <ImageIcon className="w-4 h-4" />
                {t('add_image')}
              </button>

              <div className="ml-auto flex items-center gap-1">
                <button
                  onClick={() => {
                    setZoom((z) => Math.max(0.5, Number((z - 0.25).toFixed(2))));
                  }}
                  aria-label={t('zoom_out')}
                  title={t('zoom_out')}
                  className="p-2 rounded-xl border border-[var(--color-border)] hover:border-[var(--color-text-muted)]"
                  data-testid="pdf-zoom-out"
                >
                  <ZoomOut className="w-4 h-4" />
                </button>
                <span className="text-xs text-[var(--color-text-muted)] w-12 text-center">
                  {Math.round(zoom * 100)}%
                </span>
                <button
                  onClick={() => {
                    setZoom((z) => Math.min(3, Number((z + 0.25).toFixed(2))));
                  }}
                  aria-label={t('zoom_in')}
                  title={t('zoom_in')}
                  className="p-2 rounded-xl border border-[var(--color-border)] hover:border-[var(--color-text-muted)]"
                  data-testid="pdf-zoom-in"
                >
                  <ZoomIn className="w-4 h-4" />
                </button>
              </div>
            </div>

            {tool === 'select' && mine.length > 0 && (
              <p
                className="text-xs text-[var(--color-text-muted)] mb-4"
                data-testid="pdf-hint-move"
              >
                {t('hint_move')}
              </p>
            )}

            {tool !== 'select' && (
              <div className="flex flex-wrap items-end gap-3 mb-4 p-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-card)]">
                {tool === 'text' ? (
                  <>
                    <div className="flex-1 min-w-[220px]">
                      <label className="text-xs text-[var(--color-text-muted)] mb-1.5 block">
                        {t('text_placeholder')}
                      </label>
                      <input
                        value={text}
                        onChange={(e) => {
                          setText(e.target.value);
                        }}
                        placeholder={t('text_placeholder')}
                        className="w-full px-3 py-2 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] text-sm focus:outline-none focus:border-[var(--color-text)]"
                        data-testid="pdf-text"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[var(--color-text-muted)] mb-1.5 block">
                        {t('text_size')}
                      </label>
                      <input
                        type="number"
                        min={6}
                        max={96}
                        value={textSize}
                        onChange={(e) => {
                          setTextSize(Number(e.target.value));
                        }}
                        className="w-20 px-3 py-2 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] text-sm"
                        data-testid="pdf-text-size"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[var(--color-text-muted)] mb-1.5 block">
                        {t('text_color')}
                      </label>
                      <input
                        type="color"
                        value={textColor}
                        onChange={(e) => {
                          setTextColor(e.target.value);
                        }}
                        className="w-10 h-10 rounded-lg border border-[var(--color-border)] cursor-pointer"
                      />
                    </div>
                    <p className="text-xs text-[var(--color-text-muted)] flex-1 min-w-[200px]">
                      {t('hint_text')}
                    </p>
                  </>
                ) : tool === 'draw' ? (
                  <>
                    <div>
                      <label className="text-xs text-[var(--color-text-muted)] mb-1.5 block">
                        {t('draw_color')}
                      </label>
                      <input
                        type="color"
                        value={drawColor}
                        onChange={(e) => {
                          setDrawColor(e.target.value);
                        }}
                        className="w-10 h-10 rounded-lg border border-[var(--color-border)] cursor-pointer"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[var(--color-text-muted)] mb-1.5 block">
                        {t('draw_width')}
                      </label>
                      <input
                        type="number"
                        min={1}
                        max={30}
                        value={drawWidth}
                        onChange={(e) => {
                          setDrawWidth(Number(e.target.value));
                        }}
                        className="w-20 px-3 py-2 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] text-sm"
                        data-testid="pdf-draw-width"
                      />
                    </div>
                    <p className="text-xs text-[var(--color-text-muted)] flex-1 min-w-[200px]">
                      {t('hint_draw')}
                    </p>
                  </>
                ) : (
                  <>
                    <div>
                      <label className="text-xs text-[var(--color-text-muted)] mb-1.5 block">
                        {t('fill_color')}
                      </label>
                      <input
                        type="color"
                        value={fillColor}
                        onChange={(e) => {
                          setFillColor(e.target.value);
                        }}
                        className="w-10 h-10 rounded-lg border border-[var(--color-border)] cursor-pointer"
                      />
                    </div>
                    <p className="text-xs text-[var(--color-text-muted)] flex-1 min-w-[200px]">
                      {t('hint_fill')}
                    </p>
                  </>
                )}
              </div>
            )}

            <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-card)] p-4 overflow-auto max-h-[75vh]">
              {big ? (
                <div
                  className="relative mx-auto bg-white shadow-lg"
                  style={{
                    width: big.width,
                    maxWidth: '100%',
                    aspectRatio: `${String(big.width)}/${String(big.height)}`,
                  }}
                  onMouseDown={onCanvasDown}
                  onMouseMove={onCanvasMove}
                  onMouseUp={onCanvasUp}
                  onMouseLeave={onCanvasUp}
                  onClick={onCanvasClick}
                  data-cursor={tool}
                  data-testid="pdf-canvas"
                >
                  {big.url === '' ? null : (
                    <img
                      src={big.url}
                      alt=""
                      className="w-full h-auto select-none"
                      draggable={false}
                    />
                  )}

                  {mine.map((item) =>
                    item.kind === 'draw' ? (
                      // Росчерк показан SVG поверх страницы и не ловит мышь: иначе
                      // его прямоугольник мешал бы рисовать рядом с уже нарисованным.
                      <svg
                        key={item.id}
                        className="absolute inset-0 w-full h-full pointer-events-none"
                        viewBox="0 0 1 1"
                        preserveAspectRatio="none"
                        data-testid="pdf-overlay"
                      >
                        <polyline
                          points={(item.points ?? [])
                            .map((p) => `${String(p.x)},${String(p.y)}`)
                            .join(' ')}
                          fill="none"
                          stroke={item.color}
                          strokeWidth={item.size}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          vectorEffect="non-scaling-stroke"
                        />
                      </svg>
                    ) : (
                      <span
                        key={item.id}
                        onMouseDown={(e) => {
                          onOverlayDown(e, item);
                        }}
                        onClick={(e) => {
                          // Щелчок по наложению больше ничего не удаляет:
                          // курсор обещает перенос, а любое зажатие без
                          // заметного сдвига браузер докладывает как click,
                          // и картинка исчезала прямо под рукой. Убрать
                          // наложение можно кнопкой в списке справа.
                          e.stopPropagation();
                        }}
                        title={t('hint_move')}
                        data-testid="pdf-overlay"
                        // Под кистью, заливкой и надписью наложение не ловит
                        // мышь: иначе поверх вставленной картинки нельзя было
                        // бы ни нарисовать, ни закрасить.
                        className={
                          tool === 'select'
                            ? 'absolute cursor-move'
                            : 'absolute pointer-events-none'
                        }
                        style={{
                          left: `${String(item.x * 100)}%`,
                          top: `${String(item.y * 100)}%`,
                          width: item.kind === 'text' ? undefined : `${String(item.width * 100)}%`,
                          height:
                            item.kind === 'fill'
                              ? `${String((item.height ?? 0) * 100)}%`
                              : undefined,
                          background: item.kind === 'fill' ? item.color : undefined,
                          color: item.kind === 'text' ? item.color : undefined,
                          // Кегль задан в пунктах страницы, а показана она в
                          // своём масштабе: без пересчёта надпись на экране
                          // не совпала бы с надписью в готовом файле.
                          fontSize:
                            item.kind === 'text'
                              ? `${String(item.size * 1.5 * zoom)}px`
                              : undefined,
                          lineHeight: 1.3,
                          whiteSpace: 'pre',
                        }}
                      >
                        {item.kind === 'text' ? item.value : null}
                        {item.kind === 'image' ? (
                          <img
                            src={item.value}
                            alt=""
                            className="w-full h-auto"
                            draggable={false}
                          />
                        ) : null}
                      </span>
                    ),
                  )}
                </div>
              ) : (
                <p className="py-20 text-center text-[var(--color-text-muted)]">{t('drawing')}</p>
              )}
            </div>
          </div>

          {/* Действия над документом целиком. */}
          <aside className="lg:w-56 shrink-0 space-y-6">
            <div>
              <h2 className="text-sm font-medium text-[var(--color-text-muted)] mb-2">
                {t('range_title')}
              </h2>
              <div className="flex gap-2">
                <input
                  value={range}
                  onChange={(e) => {
                    setRange(e.target.value);
                  }}
                  placeholder={t('range_placeholder')}
                  className="w-full px-3 py-2 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] text-sm focus:outline-none focus:border-[var(--color-text)]"
                  data-testid="pdf-range"
                />
                <button
                  onClick={keepRange}
                  className="px-3 py-2 rounded-xl border border-[var(--color-border)] text-sm hover:border-[var(--color-text-muted)]"
                  data-testid="pdf-extract"
                >
                  {t('extract')}
                </button>
              </div>
            </div>

            {mine.length > 0 && (
              <div>
                <h2 className="text-sm font-medium text-[var(--color-text-muted)] mb-2">
                  {t('overlay_list')}
                </h2>
                <ul className="space-y-1.5">
                  {mine.map((item) => (
                    <li key={item.id} className="flex items-center justify-between gap-2 text-xs">
                      <span className="truncate text-[var(--color-text-muted)]">
                        {item.kind === 'text' ? item.value : t(`overlay_kind_${item.kind}`)}
                      </span>
                      <button
                        onClick={() => {
                          removeOverlay(item.id);
                        }}
                        aria-label={t('overlay_remove')}
                        className="p-1 rounded hover:bg-[var(--color-bg-elevated)]"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <p className="text-xs text-[var(--color-text-muted)] leading-relaxed">
              {t('privacy_note')}
            </p>
          </aside>
        </div>
      )}
    </div>
  );
}
